<script setup lang="ts">
import BaseDropdownMenu from "@/components/Base/Dropdown/Menu.vue";
import { type MenuDirection } from "@/components/Base/Dropdown/Menu.vue";
import IconButton from "@/components/IconButton.vue";
import { useItemQueries } from "@/composables/useItemQueries";
import { useUserLimits } from "@/composables/useUserLimits";
import type {
  App,
  Folder,
  Item,
  UserLimitEnum,
} from "@/services/fobizzToolsApi";
import type { MenuItem } from "@/types/folders";

const props = defineProps<{
  folder?: Folder;
  label?: string;
  direction?: MenuDirection;
}>();

const { dropdownItems } = useTools();
const { t } = useI18n();
const router = useRouter();

const { userLimits } = useUserLimits();

const { useCreateMutation } = useItemQueries();
const { getShowUrl } = useToolRouting();

// Limit check
const limitReached = (app: App) => {
  return userLimits.value.some(
    (limit) =>
      // Forcing type cast here. They are equal in 90% of the cases. More refactoring is needed to fully decouple apps from limits.
      limit.kind === (app.id as unknown as UserLimitEnum) && limit.limitReached
  );
};

// Create handler
const handleCreate = (app: App) => {
  if (limitReached(app) || !app.id) return;

  useCreateMutation().mutate(
    {
      objType: app.id,
      folderId: props.folder?.id,
    },
    {
      onSuccess: (data: Item) => {
        const url = getShowUrl(app.id, data.id) || "/";
        router.push(url);
      },
    }
  );
};

// Transform dropdown items
const enhancedItems = computed<MenuItem[]>(() =>
  dropdownItems.value.map((section) => ({
    ...section,
    children: section.children?.map((item): MenuItem => {
      // Explicit return type
      if (!item.appData) return item;

      const app = item.appData;

      // Attach clickHandler if the item can create an instance
      if ("id" in app && app.supportsCreateNewInstance) {
        item.to = undefined;
        item.href = undefined;
        item.onClick = () => handleCreate(app);
      }

      return {
        ...item,
        // Ensure all required MenuItem properties are present
        disabled: item.disabled || ("id" in app ? limitReached(app) : false),
      };
    }),
  }))
);
</script>

<template>
  <div class="dropdown">
    <IconButton
      id="create-menu"
      :rounded="true"
      icon-name="plus-lg"
      size="sm"
      kind="primary"
      icon-position="left"
      type="button"
      data-bs-toggle="dropdown"
      data-testid="button-create"
      :aria-label="t('links.create')"
      aria-expanded="false"
      class="hide-sm"
    >
      <span>{{ label ?? t("buttons.createContent") }}</span>
    </IconButton>

    <BaseDropdownMenu
      class="dropdown-menu-end"
      :items="enhancedItems"
      :direction="direction"
    />
  </div>
</template>

<style scoped lang="scss">
.dropdown-submenu {
  position: relative;

  .dropdown-menu {
    position: absolute;
    top: 0;
    right: 100%;
    min-width: 12rem;
    margin-top: -0.5rem;
    margin-right: 0.1rem;
    display: none;
  }

  &:hover > .dropdown-menu,
  &.show > .dropdown-menu {
    display: block;
  }
}

.create-dropdown {
  &__icon {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: $font-size-xs;
    flex-shrink: 0;
    margin-right: 0.625rem;
    width: 20px;
    height: 20px;
    border-radius: 1px;
    margin-top: 2px;
  }

  &__link {
    display: flex;
    text-decoration: none;
    color: $gray-600;
    white-space: nowrap;
  }
}

.disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
